import {  useState } from "react";
import "./css/SigningFlow.css";

export default function GuardianDetailsScreen({
    flowData,
    updateFlowData,
    flowContext,
    updateFlowContext,
    next,
    back,
    loading,
    setLoading,
    error,
    setError,
}) {
    
    const [sharing, setSharing] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const [linkActionTaken, setLinkActionTaken] = useState(false);
    const [guardianLinkActionType, setGuardianLinkActionType] =
        useState(null);

    const guardian = flowData.guardian || {
        confirmed: false,
    };
    
    //Authorization message must come from backend.
    const MIN_GUARDIAN_AUTHORIZATION_MESSAGE_LENGTH = 20;    
    const guardianAuthorizationMessage =
        flowData?.guardianAuthorizationMessage?.trim() || "";
    if (
        guardianAuthorizationMessage.length <
        MIN_GUARDIAN_AUTHORIZATION_MESSAGE_LENGTH
    ) {
        return (
            <div className="page">
                <div className="page-content">
                    <div className="text-red-600 mt-2">
                        Guardian authorization message is unavailable or
                        invalid. Please contact support.
                    </div>
                </div>
            </div>
        );
    }
   


    const child = flowData?.child || {};
    const childFullName = [
        child.firstName,
        child.lastName
    ].filter(Boolean).join(" ");    

    const getMinorName = () =>
        childFullName ||
        "Your child";

    const validateGuardianConsentUrl = (value) => {
        if (typeof value !== "string" || !value.trim()) {
            throw new Error(
                "Guardian consent link has not been generated. Please try again."
            );
        }

        let url;

        try {
            url = new URL(value.trim());
        } catch {
            throw new Error(
                "The guardian consent link is invalid. Please try again."
            );
        }

        if (
            url.protocol !== "https:" ||
            !url.pathname.includes("/guardian/")
        ) {
            throw new Error(
                "The guardian consent link must be a valid HTTPS guardian URL."
            );
        }

        const segments = url.pathname.split("/").filter(Boolean);
        const guardianIndex = segments.indexOf("guardian");
        const token = segments[segments.length - 1];

        if (
            guardianIndex === -1 ||
            guardianIndex >= segments.length - 1 ||
            !token ||
            !/^[a-zA-Z0-9_-]+$/.test(token)
        ) {
            throw new Error(
                "The guardian consent link does not contain a valid URL token."
            );
        }

        return url.toString();
    };

    // Validate the URL for display as well as for sharing.
    let consentUrl = "";
    let consentUrlError = "";

    try {
        consentUrl = validateGuardianConsentUrl(
            flowData?.guardianConsentUrl
        );
    } catch (err) {
        consentUrlError = err.message;
    }

    const getShareMessage = (url) =>
        `${getMinorName()} needs your parent/guardian consent to complete a waiver. Review and sign here: ${url}`;

    const copyToClipboard = async (text) => {
        if (
            !navigator.clipboard ||
            typeof navigator.clipboard.writeText !== "function"
        ) {
            throw new Error(
                "Clipboard access is unavailable. Please use Share Consent Link or copy the link manually."
            );
        }

        await navigator.clipboard.writeText(text);
    };

    const handleCopyLink = async () => {
        setError("");
        setSuccessMessage("");

        if (!consentUrl) {
            setError(consentUrlError);
            return;
        }

        try {
            await copyToClipboard(consentUrl);
            setLinkActionTaken(true);
            setGuardianLinkActionType("COPY_LINK");
            setSuccessMessage("Guardian consent link copied successfully.");
        } catch (err) {
            setError(err.message || "Unable to copy the guardian link.");
        }
    };

    const handleCopyMessage = async () => {
        setError("");
        setSuccessMessage("");

        if (!consentUrl) {
            setError(consentUrlError);
            return;
        }


        try {
            await copyToClipboard(getShareMessage(consentUrl));

            setLinkActionTaken(true);
            setGuardianLinkActionType("COPY_MESSAGE");

            setSuccessMessage(
                "Complete consent message copied. You can now paste it into a message."
            );
        } catch (err) {
            setError(err.message || "Unable to copy the consent message.");
        }
    };

    const handleShareConsentLink = async () => {
        if (loading || sharing) return;

        setError("");
        setSuccessMessage("");

        if (!guardian.confirmed) {
            setError(
                "Please confirm the guardian declaration before continuing."
            );
            return;
        }

        if (!consentUrl) {
            setError(consentUrlError);
            return;
        }



        if (typeof navigator.share !== "function") {
            // Fallback copies the complete message, not just the URL.
            await handleCopyMessage();
            return;
        }

        setSharing(true);

        try {
            // The URL is already included in text.
            // Avoid passing it separately as `url`, which can cause
            // some share targets to append the URL a second time.
            await navigator.share({
                title: "Guardian Consent Required",
                text: getShareMessage(consentUrl),
            });

            setLinkActionTaken(true);
            setGuardianLinkActionType("SHARE_MESSAGE");

            setSuccessMessage("Consent message shared successfully.");
        } catch (err) {
            // The user closing the native share sheet is not an error.
            if (err?.name === "AbortError") return;

            setError(
                err?.message || "Unable to share the consent message."
            );
        } finally {
            setSharing(false);
        }
    };

    const handleGuardianConsentChange = (e) => {
        setError("");
        setSuccessMessage("");

        updateFlowData({
            guardian: {
                ...guardian,
                confirmed: e.target.checked,
            },
        });
    };

    const handleViewGuardianResponse = async () => {
        setError("");
        setSuccessMessage("");

        if (!guardian.confirmed) {
            setError("Please confirm the guardian declaration first.");
            return;
        }

        if (!linkActionTaken || !guardianLinkActionType) {
            setError("Please share or copy the guardian message or link first.");
            return;
        }

        try {
            const response = await next({
                action: "view_guardian_response",

                guardian: {
                    ...guardian,
                    confirmed: true,
                },

                guardianLinkActionTaken: true,
                guardianLinkActionType,
            });

            if (!response || response.success === false) {
                setError(
                    response?.message ||
                        "Unable to open the guardian response screen."
                );
            }

            // On success, the flow manager should render GUARDIAN_WAITING.
        } catch (err) {
            setError(
                err?.message ||
                    "Unable to open the guardian response screen"
            );
        }
    };

    return (
        <div className={`page ${loading ? "page-loading" : ""}`}>
            {(loading || sharing) && (
                <div className="loading-overlay">
                    {sharing ? "Opening share options..." : "Loading..."}
                </div>
            )}

            {error && (
                <div className="text-red-600 mt-2" role="alert">
                    {error}
                </div>
            )}

            {successMessage && (
                <div className="text-green-700 mt-2" role="status">
                    {successMessage}
                </div>
            )}

            <div className="page-header">
                <h2 className="business-name">
                    {flowContext.selectedService?.name
                        ? `${flowContext.selectedService.name} at ${flowContext.business?.businessName}`
                        : flowContext.business?.businessName}
                </h2>
            </div>

            <div className="page-content">
                <h3 className="form-title">Guardian Details</h3>

                
                <div className="guardian-checkbox-card">
                    <div className="mb-2 font-semibold">
                        Step 1: Confirm the declaration
                    </div>

                    <div className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            id="guardianConsent"
                            checked={guardian.confirmed === true}
                            onChange={handleGuardianConsentChange}
                        />

                        <label htmlFor="guardianConsent">
                            {guardianAuthorizationMessage}
                        </label>
                    </div>
                </div>

                <div className="mt-5">
                    <div className="font-semibold mb-2">
                        Step 2: Share the guardian consent request
                    </div>

                    <p className="mb-3">
                        After confirming the declaration above, choose one of the
                        following options to share the consent request with your
                        parent or legal guardian.
                    </p>

                    <div className="guardian-sharing-actions">
                        <button
                            type="button"
                            className="secondary-button guardian-action-button"
                            onClick={handleShareConsentLink}
                            disabled={
                                !guardian.confirmed ||
                                !consentUrl ||
                                loading ||
                                sharing
                            }
                        >
                            {sharing ? "Opening share options..." : "Share Message"}
                        </button>

                        <button
                            type="button"
                            className="secondary-button guardian-action-button"
                            onClick={handleCopyLink}
                            disabled={
                                !guardian.confirmed ||
                                !consentUrl ||
                                loading ||
                                sharing
                            }
                        >
                            Copy Link
                        </button>

                        <button
                            type="button"
                            className="secondary-button guardian-action-button"
                            onClick={handleCopyMessage}
                            disabled={
                                !guardian.confirmed ||
                                !consentUrl ||
                                loading ||
                                sharing
                            }
                        >
                            Copy Message
                        </button>
                    </div>

                    {!guardian.confirmed && (
                        <p className="guardian-action-hint">
                            Confirm the declaration above to enable these options.
                        </p>
                    )}
                </div>

            </div>

            <div className={"mt-4"}>
                {"After you confirm the declaration, copy or share the consent link with your parent or legal guardian. Once you copy or share it, the declaration cannot be withdrawn because the guardian may have already started the verification process."}
            </div>

            <div className="page-footer">
                
                <button
                    className="primary-button"
                    disabled={
                        !guardian.confirmed ||
                        !linkActionTaken ||
                        !guardianLinkActionType ||
                        loading ||
                        sharing
                    }
                    onClick={handleViewGuardianResponse}
                >
                    View Guardian Response
                    <span className="button-arrow">›</span>
                </button>


                <div className="powered-by">
                    Powered by <strong>DidYouSign</strong>
                </div>
            </div>
        </div>
    );
}
